import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  Check,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  XCircle,
  Brain,
  Loader2,
  Sparkles,
  RotateCcw,
} from "lucide-react";
import { StrategicProcessingSequence } from "@/components/processing/strategic-processing-sequence";
import { MediaAllocationLoading } from "@/components/campaign-wizard/media-allocation-loading";
import { buildCampaignPlanPayload, type CampaignPlanDraftFields } from "@/lib/campaign-plan-payload";
import { generateCampaignPlanReport } from "@/lib/generate-campaign-plan";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  CampaignPlanSummary,
  CampaignPlanStageStrategy,
  AdUnitSelection,
  FunnelStrategyData,
  MediaMixConfig,
  AdUnitPlan,
  PlannerPersona,
  MediaAllocation,
  MediaAllocationPlan,
} from "@/lib/campaign-draft";
import type { Product, Persona, Brand } from "@shared/schema";
import type { NarrativeOpportunity, NarrativeAngle } from "@/lib/campaign-draft";

interface CampaignPlanStepProps {
  campaignPlan: CampaignPlanSummary | null;
  plannerPersona: PlannerPersona | null;
  mediaAllocation: MediaAllocation | null;
  adUnitSelections: AdUnitSelection[];
  funnelStrategy: FunnelStrategyData | null;
  mediaMix: MediaMixConfig | null;
  adUnitPlan: AdUnitPlan | null;
  campaignName: string;
  product: Product | null;
  persona: Persona | null;
  narrativeOpportunity: NarrativeOpportunity | null;
  narrativeAngles: NarrativeAngle[];
  brand: Brand | null;
  onUpdate: (plan: CampaignPlanSummary | null) => void;
  onUpdatePlannerPersona: (persona: PlannerPersona | null) => void;
  onUpdateMediaAllocation: (allocation: MediaAllocation | null) => void;
  isStale: boolean;
  onClearStale: () => void;
  onNext?: () => void;
}

const STATUS_CLASSES: Record<string, string> = {
  approved: "bg-green-500/10 text-green-600 border-green-500/20",
  needs_revision: "bg-destructive/10 text-destructive border-destructive/20",
  generated: "bg-primary/10 text-primary border-primary/20",
  draft: "bg-muted text-muted-foreground border-border",
  stale: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
};

const STATUS_LABEL: Record<string, string> = {
  approved: "Approved",
  needs_revision: "Needs Revision",
  generated: "Generated",
  draft: "Draft",
  stale: "Stale",
};

const PROCESSING_MESSAGES = [
  "Reviewing campaign architecture",
  "Aligning funnel narrative",
  "Consolidating stage logic",
  "Translating structure into strategy",
  "Finalizing executive summary",
  "Mapping why this campaign wins",
  "Assembling final report",
];

export function CampaignPlanStep(props: CampaignPlanStepProps) {
  const {
    campaignPlan,
    plannerPersona,
    mediaAllocation,
    campaignName,
    brand,
    product,
    persona,
    narrativeOpportunity,
    narrativeAngles,
    funnelStrategy,
    mediaMix,
    adUnitSelections,
    adUnitPlan,
    onUpdate,
    onUpdatePlannerPersona,
    onUpdateMediaAllocation,
    isStale,
    onClearStale,
    onNext,
  } = props;

  const [isGenerating, setIsGenerating] = useState(false);
  const [apiDone, setApiDone] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isPlannerGenerating, setIsPlannerGenerating] = useState(false);
  const [plannerError, setPlannerError] = useState<string | null>(null);
  const [isMediaAllocationGenerating, setIsMediaAllocationGenerating] = useState(false);
  const [mediaAllocationError, setMediaAllocationError] = useState<string | null>(null);
  const { toast } = useToast();
  const apiResultRef = useRef<CampaignPlanSummary | null>(null);

  const handleGeneratePlanner = async () => {
    setPlannerError(null);
    setIsPlannerGenerating(true);
    try {
      const result = await apiRequest("POST", "/api/generate-planner-persona", {
        product: product ?? null,
        persona: persona ?? null,
        narrative_opportunity: narrativeOpportunity ?? null,
        narrative_angles: narrativeAngles.length > 0 ? narrativeAngles : [],
      });
      const data = await result.json();
      const extracted: PlannerPersona | null =
        data?.planner_persona ?? data?.output?.planner_persona ?? null;
      if (!extracted) throw new Error("No planner_persona in response");
      onUpdatePlannerPersona(extracted);
      toast({ title: "AI Planner ready", description: `Meet ${extracted.name} — your campaign strategist.` });
    } catch (err: any) {
      setPlannerError(err?.message ?? "Failed to generate planner. Try again.");
    } finally {
      setIsPlannerGenerating(false);
    }
  };

  const handleGenerateMediaAllocation = async () => {
    setMediaAllocationError(null);
    setIsMediaAllocationGenerating(true);
    try {
      const result = await apiRequest("POST", "/api/generate-media-allocation", {
        product: product ?? null,
        persona: persona ?? null,
        narrative_opportunity: narrativeOpportunity ?? null,
        narrative_angles: narrativeAngles.length > 0 ? narrativeAngles : [],
        funnel_strategy: funnelStrategy ?? null,
        media_mix: mediaMix ?? null,
        campaign_plan: campaignPlan ?? null,
        planner_persona: plannerPersona ?? null,
      });
      const data = await result.json();
      const emptyExec = { summary: "", key_points: [] };
      const extracted: MediaAllocation | null =
        data?.allocation_plan
          ? { allocation_plan: data.allocation_plan, executive_summary: data.executive_summary ?? emptyExec }
          : (data?.output?.allocation_plan
            ? { allocation_plan: data.output.allocation_plan, executive_summary: data.output.executive_summary ?? emptyExec }
            : null);
      if (!extracted) throw new Error("No allocation_plan in response");
      onUpdateMediaAllocation(extracted);
      if (campaignPlan && campaignPlan.funnelOverview) {
        const allocationStageMap: Record<string, string> = {
          awareness: "Awareness",
          interest: "Knowledge",
          consideration: "Liking",
          intent: "Preference",
          evaluation: "Conviction",
          purchase: "Purchase",
        };
        const updatedOverview = campaignPlan.funnelOverview.map((item) => {
          const matchKey = Object.entries(allocationStageMap).find(
            ([, label]) => label.toLowerCase() === item.stage.toLowerCase()
          )?.[0];
          if (matchKey && extracted.allocation_plan[matchKey as keyof MediaAllocationPlan]) {
            const ad = extracted.allocation_plan[matchKey as keyof MediaAllocationPlan].ad_types;
            const total = (ad.video ?? 0) + (ad.static ?? 0) + (ad.carousel ?? 0);
            return { ...item, assetCount: total };
          }
          return item;
        });
        const newTotal = updatedOverview.reduce((s, o) => s + (o.assetCount ?? 0), 0);
        onUpdate({ ...campaignPlan, funnelOverview: updatedOverview, totalAdUnits: newTotal });
      }
      toast({ title: "Media Allocation Strategy ready", description: "Your ad mix across funnel stages is set." });
    } catch (err: any) {
      setMediaAllocationError(err?.message ?? "Failed to generate media allocation. Try again.");
    } finally {
      setIsMediaAllocationGenerating(false);
    }
  };

  const resolvedCampaignName = (() => {
    if (campaignName && campaignName !== "Untitled Campaign") return campaignName;
    const parts: string[] = [];
    if (persona?.name) parts.push(persona.name);
    if (product?.name) parts.push(product.name);
    if (narrativeOpportunity?.title) parts.push(narrativeOpportunity.title);
    return parts.length > 0 ? parts.join(" x ") : "Campaign Plan";
  })();

  const handleGenerate = async () => {
    setApiError(null);
    apiResultRef.current = null;
    setApiDone(false);
    setIsGenerating(true);

    try {
      const fields: CampaignPlanDraftFields = {
        product,
        persona,
        narrativeOpportunity,
        narrativeAngles,
        funnelStrategy,
        mediaMix,
        adUnitSelections,
        adUnitPlan,
      };
      const payload = buildCampaignPlanPayload(fields, brand);
      const plan = await generateCampaignPlanReport(payload, resolvedCampaignName, mediaMix?.totalUnits);
      apiResultRef.current = plan;
      setApiDone(true);
      onUpdateMediaAllocation(null);
    } catch (err: any) {
      const message = err?.message ?? "Unable to compile the campaign plan right now. Please try again.";
      console.error("[CampaignPlan] Generation failed:", err);
      setApiError(message);
      setIsGenerating(false);
    }
  };

  const handleAnimationComplete = () => {
    if (!apiDone || !apiResultRef.current) return;
    setIsGenerating(false);
    setApiDone(false);
    onUpdate(apiResultRef.current);
    onClearStale();
    toast({ title: "Campaign plan compiled", description: "Your campaign strategy report is ready." });
  };

  const handleApprove = () => {
    if (!campaignPlan) return;
    onUpdate({ ...campaignPlan, status: "approved" });
  };

  const handleRequestRevision = () => {
    if (!campaignPlan) return;
    onUpdate({ ...campaignPlan, status: "needs_revision" });
  };

  const hasRichContent =
    campaignPlan?.stageStrategy && campaignPlan.stageStrategy.length > 0;

  if (isGenerating) {
    return (
      <StrategicProcessingSequence
        title="Compiling Campaign Plan"
        subtitle="Translating funnel strategy, media mix, and ad architecture into a unified campaign narrative"
        isRunning={isGenerating}
        onComplete={handleAnimationComplete}
        waitForApi={!apiDone}
        statusMessages={PROCESSING_MESSAGES}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
          Campaign Plan
        </p>
        <h2 className="text-xl font-bold tracking-tight" data-testid="text-campaign-plan-title">
          {campaignPlan ? (campaignPlan.name || resolvedCampaignName) : "Compile your plan"}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {campaignPlan
            ? "Full-funnel strategy report ready for review"
            : "Generate a complete stage-by-stage strategy from your draft selections"}
        </p>
      </div>

      {isStale && campaignPlan && (
        <div className="flex items-center gap-2 rounded-xl border border-yellow-500/20 bg-yellow-500/[0.05] p-3">
          <AlertTriangle className="w-4 h-4 text-yellow-600 shrink-0" />
          <span className="text-sm text-yellow-700 dark:text-yellow-400" data-testid="text-plan-stale-warning">
            Upstream selections changed. Consider regenerating the campaign plan.
          </span>
        </div>
      )}

      {/* ── Planner Persona Section ── */}
      <PlannerSection
        planner={plannerPersona}
        isGenerating={isPlannerGenerating}
        error={plannerError}
        onGenerate={handleGeneratePlanner}
        onRegenerate={() => {
          // Regenerating the AI Planner invalidates everything downstream
          // (the stage-by-stage strategy and the media allocation are both
          // derived from the planner persona), so clear them too. The user
          // must re-run those steps with the new planner.
          onUpdatePlannerPersona(null);
          onUpdate(null);
          onUpdateMediaAllocation(null);
          apiResultRef.current = null;
          setApiDone(false);
          setApiError(null);
          setMediaAllocationError(null);
          handleGeneratePlanner();
        }}
      />

      {apiError && (
        <div className="flex items-start gap-2 rounded-xl border border-destructive/20 bg-destructive/[0.05] p-4">
          <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-destructive">Compilation failed</p>
            <p className="text-sm text-destructive/80" data-testid="text-plan-error">
              {apiError}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerate}
              className="mt-2 rounded-full"
              data-testid="button-retry-plan"
            >
              Try again
            </Button>
          </div>
        </div>
      )}

      {!campaignPlan && !apiError && (
        <Card className="rounded-2xl border-dashed" data-testid="card-compile-plan-empty">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/[0.07] border border-primary/15 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-primary/60" />
            </div>
            <div>
              <h3 className="font-semibold text-base mb-1">Compile Campaign Plan</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Generate a complete stage-by-stage strategy report from your funnel strategy, media mix, and ad unit selections.
              </p>
            </div>
            <Button onClick={handleGenerate} className="rounded-full gap-2" data-testid="button-compile-plan">
              <Sparkles className="h-4 w-4" />
              Compile Campaign Plan
            </Button>
          </CardContent>
        </Card>
      )}

      {campaignPlan && (
        <div className="space-y-5">
          <PlanHeader
            plan={campaignPlan}
            mediaMix={mediaMix}
            onApprove={handleApprove}
            onRequestRevision={handleRequestRevision}
          />

          {campaignPlan.executiveSummary && (
            <ExecutiveSummarySection
              summary={campaignPlan.executiveSummary}
              keyTakeaways={campaignPlan.keyTakeaways ?? []}
            />
          )}

          {!mediaAllocation && campaignPlan.funnelOverview && campaignPlan.funnelOverview.length > 0 && (
            <FunnelOverviewSection overview={campaignPlan.funnelOverview} />
          )}

          {hasRichContent && (
            <StageStrategiesSection stages={campaignPlan.stageStrategy!} />
          )}

          {campaignPlan.whyThisCampaignWins && campaignPlan.whyThisCampaignWins.length > 0 && (
            <WhyThisWinsSection reasons={campaignPlan.whyThisCampaignWins} />
          )}

          <div className="flex items-center justify-end gap-3 pt-2 flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onUpdate(null);
                onUpdatePlannerPersona(null);
                onUpdateMediaAllocation(null);
              }}
              className="rounded-full text-muted-foreground hover:text-destructive"
              data-testid="button-reset-plan"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset Plan
            </Button>
            <Button
              variant="outline"
              onClick={handleRequestRevision}
              disabled={campaignPlan.status === "needs_revision"}
              className="rounded-full"
              data-testid="button-request-revision"
            >
              Request Revision
            </Button>
            <Button
              onClick={handleApprove}
              disabled={campaignPlan.status === "approved"}
              className="rounded-full"
              data-testid="button-approve-plan"
            >
              <Check className="w-4 h-4 mr-2" />
              {campaignPlan.status === "approved" ? "Approved" : "Approve Plan"}
            </Button>
          </div>
        </div>
      )}

      {isMediaAllocationGenerating ? (
        <MediaAllocationLoading />
      ) : (
        <>
          {mediaAllocationError && (
            <div className="flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/[0.05] p-3">
              <XCircle className="w-4 h-4 text-destructive shrink-0" />
              <span className="text-sm text-destructive" data-testid="text-media-allocation-error">{mediaAllocationError}</span>
            </div>
          )}

          {mediaAllocation && (
            <MediaAllocationSection allocation={mediaAllocation} />
          )}

          <div className="pt-2 flex justify-end">
            <Button
              onClick={handleGenerateMediaAllocation}
              variant="outline"
              className="rounded-full gap-2"
              data-testid="button-generate-media-allocation"
            >
              <RefreshCw className="w-4 h-4" />
              {mediaAllocation ? "Regenerate Media Allocation Strategy" : "Generate Media Allocation Strategy"}
            </Button>
          </div>
        </>
      )}

    </div>
  );
}

// ─── Media Allocation Section ────────────────────────────────────────────────

const STAGE_ORDER = ["awareness", "interest", "consideration", "intent", "evaluation", "purchase"] as const;
const STAGE_LABELS: Record<string, string> = {
  awareness: "Awareness",
  interest: "Knowledge",
  consideration: "Liking",
  intent: "Preference",
  evaluation: "Conviction",
  purchase: "Purchase",
};

function MediaAllocationSection({ allocation }: { allocation: MediaAllocation }) {
  const totals = STAGE_ORDER.reduce(
    (acc, stage) => {
      const d = allocation.allocation_plan[stage];
      if (!d) return acc;
      return {
        video: acc.video + d.ad_types.video,
        static: acc.static + d.ad_types.static,
        carousel: acc.carousel + d.ad_types.carousel,
      };
    },
    { video: 0, static: 0, carousel: 0 }
  );

  return (
    <Card className="rounded-2xl overflow-hidden" data-testid="card-media-allocation">
      {/* Animated header banner */}
      <div
        className="relative px-6 py-5 text-white overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0f2460 0%, #1d4ed8 35%, #1e3a8a 65%, #1a3372 100%)",
          backgroundSize: "250% 250%",
          animation: "gradient-shift 10s ease infinite",
        }}
      >
        {/* Dot grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.10) 1px, transparent 1px)",
            backgroundSize: "18px 18px",
          }}
        />
        {/* Scan shimmer */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute top-0 bottom-0 w-8"
            style={{
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)",
              animation: "scan-shimmer 6s ease-in-out infinite",
              animationDelay: "2s",
            }}
          />
        </div>

        <div className="relative flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="relative flex h-1.5 w-1.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cyan-400" />
            </span>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-200">
              Media Allocation Strategy
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {totals.video > 0 && (
              <span className="text-xs font-semibold bg-white/10 text-white border border-white/15 px-2.5 py-0.5 rounded-full">
                {totals.video} Video
              </span>
            )}
            {totals.static > 0 && (
              <span className="text-xs font-semibold bg-white/10 text-white border border-white/15 px-2.5 py-0.5 rounded-full">
                {totals.static} Static
              </span>
            )}
            {totals.carousel > 0 && (
              <span className="text-xs font-semibold bg-white/10 text-white border border-white/15 px-2.5 py-0.5 rounded-full">
                {totals.carousel} Carousel
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Summary + key points in normal card body */}
      {(allocation.executive_summary.summary || allocation.executive_summary.key_points?.length > 0) && (
        <div className="px-6 py-5 border-b border-border space-y-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Executive Summary</p>
          {allocation.executive_summary.summary && (
            <p className="text-sm leading-relaxed text-foreground">{allocation.executive_summary.summary}</p>
          )}
          {allocation.executive_summary.key_points?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {allocation.executive_summary.key_points.map((point, i) => (
                <div key={i} className="flex items-start gap-2 rounded-xl bg-muted/50 px-3 py-2.5">
                  <span className="mt-0.5 w-4 h-4 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                    {i + 1}
                  </span>
                  <p className="text-xs leading-relaxed text-foreground">{point}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stage rows */}
      <div className="px-5 py-4 space-y-3">
        {STAGE_ORDER.map((stage, i) => {
          const stageData = allocation.allocation_plan[stage];
          if (!stageData) return null;
          const carousel = stageData.ad_types?.carousel ?? 0;
          const staticCount = stageData.ad_types?.static ?? 0;
          const video = stageData.ad_types?.video ?? 0;
          const total = carousel + staticCount + video;
          if (total === 0) return null;

          return (
            <div
              key={stage}
              className="rounded-xl border border-border bg-muted/30 p-4 space-y-3"
              data-testid={`media-allocation-stage-${stage}`}
            >
              {/* Stage header */}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5">
                  <span className="text-[10px] font-bold w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <div>
                    <span className="font-semibold text-sm text-foreground">{STAGE_LABELS[stage]}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{total} {total === 1 ? "ad" : "ads"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {video > 0 && (
                    <span className="text-[11px] font-semibold bg-violet-500/10 text-violet-600 border border-violet-500/15 px-2 py-0.5 rounded-full">
                      {video} Video
                    </span>
                  )}
                  {staticCount > 0 && (
                    <span className="text-[11px] font-semibold bg-sky-500/10 text-sky-600 border border-sky-500/15 px-2 py-0.5 rounded-full">
                      {staticCount} Static
                    </span>
                  )}
                  {carousel > 0 && (
                    <span className="text-[11px] font-semibold bg-indigo-500/10 text-indigo-600 border border-indigo-500/15 px-2 py-0.5 rounded-full">
                      {carousel} Carousel
                    </span>
                  )}
                </div>
              </div>

              {/* Stacked bar */}
              <div className="flex h-2.5 rounded-full overflow-hidden bg-muted gap-[2px]">
                {video > 0 && (
                  <div className="bg-violet-500 rounded-full" style={{ width: `${(video / total) * 100}%` }} />
                )}
                {staticCount > 0 && (
                  <div className="bg-sky-400 rounded-full" style={{ width: `${(staticCount / total) * 100}%` }} />
                )}
                {carousel > 0 && (
                  <div className="bg-indigo-400 rounded-full" style={{ width: `${(carousel / total) * 100}%` }} />
                )}
              </div>

              {/* Justification */}
              <p className="text-xs text-muted-foreground leading-relaxed">{stageData.justification}</p>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Planner Section ─────────────────────────────────────────────────────────

function PlannerSection({
  planner,
  isGenerating,
  error,
  onGenerate,
  onRegenerate,
}: {
  planner: PlannerPersona | null;
  isGenerating: boolean;
  error: string | null;
  onGenerate: () => void;
  onRegenerate: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  if (isGenerating) {
    return (
      <Card className="rounded-2xl overflow-hidden border-primary/20">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
          </div>
          <div className="text-center">
            <p className="font-semibold text-sm">Building your AI Planner</p>
            <p className="text-xs text-muted-foreground mt-1">Synthesizing persona, product, and narrative context…</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!planner) {
    return (
      <Card className="rounded-2xl border-dashed" data-testid="card-planner-empty">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/[0.07] border border-primary/15 flex items-center justify-center">
            <Brain className="w-6 h-6 text-primary/60" />
          </div>
          <div>
            <h3 className="font-semibold text-base mb-1">Create Your AI Marketing Planner</h3>
            <p className="text-sm text-muted-foreground max-w-xs">
              Generate a bespoke AI planning agent calibrated to your product, persona, and narrative strategy.
            </p>
          </div>
          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
          <Button onClick={onGenerate} className="rounded-full gap-2" data-testid="btn-generate-planner">
            <Sparkles className="h-4 w-4" />
            Generate AI Planner
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-0 rounded-2xl overflow-hidden border border-primary/25" data-testid="card-planner-persona">
      {/* Header gradient banner */}
      <div
        className="relative px-5 py-4 text-white overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #5b21b6 0%, #7c3aed 35%, #4c1d95 65%, #6d28d9 100%)",
          backgroundSize: "250% 250%",
          animation: "gradient-shift 10s ease infinite",
        }}
      >
        {/* Dot grid texture */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)",
            backgroundSize: "18px 18px",
          }}
        />
        {/* Scan shimmer */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute top-0 bottom-0 w-8"
            style={{
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
              animation: "scan-shimmer 5s ease-in-out infinite",
              animationDelay: "1.5s",
            }}
          />
        </div>

        <div className="relative flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-violet-200">Your AI Marketing Planner</p>
            </div>
            <h3 className="text-xl font-bold tracking-tight" data-testid="text-planner-name">{planner.name}</h3>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <span className="text-xs font-semibold bg-white/15 text-white px-2.5 py-0.5 rounded-full border border-white/20">
                {planner.archetype}
              </span>
              {planner.personality_traits?.slice(0, 3).map((t) => (
                <span key={t} className="text-xs bg-white/10 text-violet-100 px-2 py-0.5 rounded-full border border-white/10">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <button
            onClick={onRegenerate}
            className="mt-0.5 flex-shrink-0 text-violet-200 hover:text-white transition-colors relative"
            title="Regenerate planner"
            data-testid="btn-regenerate-planner"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="bg-card px-5 py-4 space-y-4">
        {/* Mission */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Mission</p>
          <p className="text-sm leading-relaxed" data-testid="text-planner-mission">{planner.mission}</p>
        </div>

        {/* Strategic mindset */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Strategic Mindset</p>
          <p className="text-sm text-muted-foreground leading-relaxed">{planner.strategic_mindset}</p>
        </div>

        {/* Signature strength */}
        {planner.signature_strength && (
          <div className="rounded-xl bg-primary/[0.05] border border-primary/15 px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-primary/70 mb-0.5">Signature Strength</p>
            <p className="text-sm font-medium">{planner.signature_strength}</p>
          </div>
        )}

        {/* Expandable detail */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {expanded ? "Hide" : "Show"} full profile
        </button>

        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4 border-t border-border pt-4"
          >
            {/* Decision framework */}
            {planner.decision_framework && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Decision Framework</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    ["Optimizes For", planner.decision_framework.what_this_planner_optimizes_for],
                    ["Avoids", planner.decision_framework.what_this_planner_avoids],
                    ["Primary Focus", planner.decision_framework.primary_focus],
                    ["Secondary Focus", planner.decision_framework.secondary_focus],
                  ].map(([label, val]) => val ? (
                    <div key={label} className="rounded-xl border border-border bg-muted/20 px-3 py-2.5">
                      <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">{label}</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{val}</p>
                    </div>
                  ) : null)}
                </div>
              </div>
            )}

            {/* Persuasion style */}
            {planner.persuasion_style && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Persuasion Style</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ["Tone", planner.persuasion_style.tone],
                    ["Risk Tolerance", planner.persuasion_style.risk_tolerance],
                    ["Narrative Preference", planner.persuasion_style.narrative_preference],
                    ["Emotional / Logical", planner.persuasion_style.emotional_vs_logical_bias],
                  ].map(([label, val]) => val ? (
                    <div key={label} className="rounded-xl border border-border bg-muted/20 px-3 py-2.5">
                      <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">{label}</p>
                      <p className="text-xs font-medium">{val}</p>
                    </div>
                  ) : null)}
                </div>
              </div>
            )}

            {/* Core beliefs */}
            {planner.core_beliefs_about_advertising?.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Core Beliefs</p>
                <ul className="space-y-1.5">
                  {planner.core_beliefs_about_advertising.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <span className="mt-1.5 flex-shrink-0 w-1 h-1 rounded-full bg-primary/50" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Blindspots */}
            {planner.blindspots_to_watch && (
              <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-yellow-600 mb-0.5">Blindspot to Watch</p>
                <p className="text-xs text-muted-foreground">{planner.blindspots_to_watch}</p>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

function PlanHeader({
  plan,
  mediaMix,
  onApprove,
  onRequestRevision,
}: {
  plan: CampaignPlanSummary;
  mediaMix?: MediaMixConfig | null;
  onApprove: () => void;
  onRequestRevision: () => void;
}) {
  const statusClass = STATUS_CLASSES[plan.status] ?? STATUS_CLASSES.draft;
  const statusLabel = STATUS_LABEL[plan.status] ?? plan.status;
  const compiledDate = plan.compiledAt
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(plan.compiledAt))
    : null;

  return (
    <Card className="rounded-2xl" data-testid="card-plan-summary">
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${statusClass}`}
                data-testid="badge-plan-status"
              >
                {plan.status === "approved" && <Check className="w-3 h-3" />}
                {statusLabel}
              </span>
              {compiledDate && (
                <span className="text-[11px] text-muted-foreground/60">
                  Compiled {compiledDate}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
              {plan.funnelOverview?.length ? (
                <span>{plan.funnelOverview.length} funnel stages</span>
              ) : null}
              {mediaMix?.totalUnits ? (
                <span>{mediaMix.totalUnits} total assets</span>
              ) : plan.totalAdUnits > 0 ? (
                <span>{plan.totalAdUnits} total assets</span>
              ) : null}
              {plan.totalAdUnits > 0 && mediaMix?.totalUnits && plan.totalAdUnits !== mediaMix.totalUnits && (
                <span>{plan.totalAdUnits} ad unit templates</span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
      {children}
    </p>
  );
}

function ExecutiveSummarySection({
  summary,
  keyTakeaways,
}: {
  summary: string;
  keyTakeaways: string[];
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 }}
      data-testid="section-executive-summary"
    >
      <SectionLabel>Executive Summary</SectionLabel>
      <Card className="rounded-2xl">
        <CardContent className="pt-5 pb-5 space-y-4">
          <p className="text-sm leading-relaxed text-foreground/80" data-testid="text-executive-summary">
            {summary}
          </p>
          {keyTakeaways.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {keyTakeaways.map((t, i) => (
                <div
                  key={i}
                  className="px-3 py-1.5 rounded-xl border border-primary/15 bg-primary/[0.04] text-[11px] text-foreground/70"
                  data-testid={`badge-takeaway-${i}`}
                >
                  {t}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function FunnelOverviewSection({
  overview,
}: {
  overview: { stage: string; role: string; assetCount?: number }[];
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      data-testid="section-funnel-overview"
    >
      <SectionLabel>Funnel Overview</SectionLabel>
      <Card className="rounded-2xl">
        <CardContent className="pt-4 pb-4">
          <div className="space-y-0">
            {overview.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 py-3 border-b border-border/50 last:border-0"
                data-testid={`row-funnel-stage-${i}`}
              >
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/[0.06] mt-0.5">
                  <span className="text-[10px] font-bold text-primary/70">{i + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{item.stage}</span>
                    {item.assetCount != null && (
                      <span className="text-[11px] text-muted-foreground">
                        {item.assetCount} {item.assetCount === 1 ? "asset" : "assets"}
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
                    {item.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function StageStrategiesSection({ stages }: { stages: CampaignPlanStageStrategy[] }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.15 }}
      data-testid="section-stage-strategies"
    >
      <SectionLabel>Stage-by-Stage Strategy</SectionLabel>
      <div className="space-y-3">
        {stages.map((stage, i) => (
          <StageStrategyCard key={i} stage={stage} index={i} />
        ))}
      </div>
    </motion.div>
  );
}

function StageStrategyCard({
  stage,
  index,
}: {
  stage: CampaignPlanStageStrategy;
  index: number;
}) {
  const [expanded, setExpanded] = useState(index === 0);

  return (
    <Card
      className="rounded-2xl overflow-hidden"
      data-testid={`card-stage-strategy-${index}`}
    >
      <button
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`button-toggle-stage-${index}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/[0.06]">
            <span className="text-[10px] font-bold text-primary/70">{index + 1}</span>
          </div>
          <span className="font-semibold text-sm">{stage.stage}</span>
          {stage.assetCount != null && (
            <span className="text-[11px] text-muted-foreground hidden sm:block">
              {stage.assetCount} {stage.assetCount === 1 ? "asset" : "assets"}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
      </button>

      {expanded && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
        >
          <CardContent className="pb-5 pt-0 space-y-5 border-t border-border/50">
            <div className="pt-4">
              <p className="text-sm leading-relaxed text-foreground/80" data-testid={`text-stage-pitch-${index}`}>
                {stage.pitch}
              </p>
            </div>

            {stage.psychologicalGoal && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  Psychological Goal
                </p>
                <p className="text-sm text-foreground/70" data-testid={`text-stage-psych-${index}`}>
                  {stage.psychologicalGoal}
                </p>
              </div>
            )}

            {stage.messagingStrategy && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  Messaging Strategy
                </p>
                <p className="text-sm text-foreground/70" data-testid={`text-stage-messaging-${index}`}>
                  {stage.messagingStrategy}
                </p>
              </div>
            )}

            {stage.copyDirections.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Copy Directions
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {stage.copyDirections.map((dir, di) => (
                    <div
                      key={di}
                      className="rounded-xl border border-border/60 bg-muted/30 px-3 py-2.5 text-[12px] text-foreground/65 leading-snug"
                      data-testid={`text-copy-direction-${index}-${di}`}
                    >
                      {dir}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {stage.transitionToNextStage && (
              <div className="flex items-start gap-2 rounded-xl border border-border/50 bg-muted/20 px-3 py-2.5">
                <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0 mt-0.5" />
                <p className="text-[12px] text-muted-foreground leading-snug" data-testid={`text-stage-transition-${index}`}>
                  <span className="font-medium text-foreground/60">Transition: </span>
                  {stage.transitionToNextStage}
                </p>
              </div>
            )}
          </CardContent>
        </motion.div>
      )}
    </Card>
  );
}

function WhyThisWinsSection({ reasons }: { reasons: string[] }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      data-testid="section-why-this-wins"
    >
      <SectionLabel>Why This Campaign Wins</SectionLabel>
      <Card className="rounded-2xl">
        <CardContent className="pt-5 pb-5">
          <ul className="space-y-3">
            {reasons.map((reason, i) => (
              <li
                key={i}
                className="flex items-start gap-2.5"
                data-testid={`text-win-reason-${i}`}
              >
                <div className="h-5 w-5 shrink-0 flex items-center justify-center rounded-full bg-primary/10 border border-primary/15 mt-0.5">
                  <Check className="w-2.5 h-2.5 text-primary" />
                </div>
                <p className="text-sm text-foreground/75 leading-snug">{reason}</p>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </motion.div>
  );
}
